import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router";
import {
  SkyNavDrawer,
  SkyCap2,
  SkyLink,
  type NavItem,
  type NavSection,
} from "@skypointcloud/skypoint-ui";
import { SlidersHorizontal as SetupIcon } from "lucide-react";
import { useMsalAuth } from "../../../hooks/useMsalAuth";
import {
  buildNavSections,
  buildNavSectionsFromConfig,
  NAV_IDS,
  type NavId,
  type SidebarConfigItem,
} from "./sidebarConfig";
import { getNavRoute, getSelectedNavItem } from "./routeMapping";
import { buildPackNavSections } from "./packNav";
import { ROUTES } from "../../../components/shared/constant";
import { usePacks } from "../../../components/packs/PackContext";
import type { PackFeatureId } from "../../../../lib/packs/types";

import { IRootReducerState } from "../../../store/IRootReducer";
import { RootState } from "../../../store";
import { useGetMyPermissionsQuery } from "../../../store/api/rbacApi";
import { useCrmRoleState, type CrmRole, type CrmRoleStatus } from "../../../hooks/usePermissions";
import {
  useGetYardiIntegrationQuery,
  useGetYardiResolutionQueueQuery,
} from "../../../store/api/yardiIntegrationsApi";
import { useGetConfigSchemaQuery } from "../../../store/api/platformConfigApi";
import { useGetCrmUnreadCountQuery } from "../../../store/api/crmInboxApi";

/**
 * H3 Inc-2 — the close-reason catalog capability, TYPED.
 *
 * `usePacks().hasFeature` takes a bare `string`, not `PackFeatureId`, so
 * `hasFeature("close_reasonz")` compiles happily and returns false for ever.
 * Annotating the constant puts the typo back on the compiler. (Type-only
 * import: pack manifests never enter the browser bundle.)
 */
const CLOSE_REASONS_FEATURE: PackFeatureId = "close_reasons";
/** PACE — the stage-funnel capability, typed for the same reason. */
const PIPELINE_FUNNEL_FEATURE: PackFeatureId = "pipeline_funnel";

interface SidebarSkyProps {
  isMobile?: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onCollapseChange?: (collapsed: boolean) => void;
}

// CRM-mode-only Settings children. Injected into the shared Settings
// group's "Settings" parent ONLY when operator_crm is the active mode,
// so they never appear in Advisor mode. Each item's route + active-state
// mapping lives in routeMapping.ts (NAV_TO_ROUTE_BASE / ROUTE_PATTERNS /
// settingsSubmenu).
//
// One entry, not five: Communication Templates, Goals, Lead Sources and
// Clinical Sync were removed because the Setup hub already indexes all four,
// and two doors to one room is what this change is about.
const CRM_SETTINGS_CHILDREN: NavItem[] = [
  { id: NAV_IDS.CRM_SETUP, label: "Setup", icon: SetupIcon },
];

/**
 * Does this caller keep the `Settings ▸ Setup` door? (doer surface, D2.)
 *
 * Every configuration surface a counselor can open from the hub is one they
 * cannot save — the six that used to 403 on Save are now gated out of the hub
 * itself — which leaves them a door onto three personal tiles (My Profile, Email
 * Signature, Notifications) they reach more directly from the avatar menu's
 * "View profile", where signature and notifications are in-page sections. So the
 * door comes off the CRM Settings menu for a counselor.
 *
 * ⚠️ POSITIVE IDENTIFICATION, NOT FAIL-CLOSED, AND THAT IS DELIBERATE. The door
 * is withheld ONLY from a caller the server has RESOLVED as `counselor`:
 *
 *   - a PLATFORM admin with no CRM person link resolves to `crmRole === null`,
 *     and hiding Setup from them would take the hub away from the people who
 *     administer it;
 *   - `loading` and `failed` are both `null` with every predicate false, and
 *     neither is a statement about the caller — a door that disappears on a
 *     backend hiccup is the same class of lie as one drawn for someone who is
 *     refused;
 *   - over-subtraction here is SILENT: nobody reports a menu entry they never saw.
 *
 * It is the opposite posture from the pack-manifest gate in
 * `server/routes/packs.ts`, which fails CLOSED on an absent role. The difference
 * is what is at stake: that gate withholds whole surfaces, this one withholds a
 * shortcut to a hub that already filters itself tile by tile. And neither is
 * access control — `/crm/setup` stays reachable by URL either way
 * (`getRequiredNavId` returns null for every `/crm/*` path).
 */
export function showsCrmSetupDoor(role: CrmRole | null, status: CrmRoleStatus): boolean {
  return !(status === "resolved" && role === "counselor");
}

/** Options for the CRM-mode Settings shaping. */
export interface CrmSettingsOptions {
  /** Default TRUE — an untouched call site must keep today's menu. */
  showSetupDoor?: boolean;
}

/**
 * Return a copy of the shared "Settings" section, CRM-mode shaped: the Settings
 * parent carries the Setup hub and NOTHING else.
 *
 * The Settings parent is built for Advisor. CRM used to inherit its children
 * wholesale, minus the few the Setup hub duplicated — which left exactly the
 * entries that have no hub card: Channels, Call Management, Activity Logs,
 * Notifications, Session, Configurations. Those are Advisor surfaces, so
 * opening one from CRM moved the operator into a different product. They stay
 * fully available in Advisor mode, where they belong and where arriving on them
 * is not a surprise.
 *
 * CRM configuration is not lost with them: the Setup hub indexes every CRM
 * surface, which is why one door is enough.
 *
 * ⚠️ THE CRM-MODE SEAM IS THE ONLY PLACE THIS MAY HAPPEN. Route ACCESS is
 * derived from `buildNavSections()` by RouteGuard, which calls that builder
 * DIRECTLY and never sees this transform. Dropping these children inside
 * `buildNavSections` would not hide a door — it would REVOKE /users, /roles,
 * /channels and the rest for every persona, Advisor mode included. Neither
 * builder is touched here; the transform runs on whichever Settings parent they
 * produced, which is also why it covers config-driven tenants.
 *
 * No-op when the section has no Settings parent (e.g. a user without settings
 * access), so Setup stays gated behind the same access as the rest of Settings.
 * Pure: never mutates the input — Advisor mode renders that same object graph
 * verbatim and must be unaffected.
 */
export function withCrmSettingsChildren(
  section: NavSection,
  { showSetupDoor = true }: CrmSettingsOptions = {},
): NavSection {
  // Setup is the ONLY child, so a caller who does not get it gets no children at
  // all rather than an empty category: SkyNavDrawer never fires onSelect for a
  // parent, so a category with nothing under it is a row that cannot be clicked.
  // The Settings parent stands as the flat item it already is — the same
  // reasoning `routes/packs.ts` applies to a nav group emptied by gating.
  //
  // ⚠️ `undefined`, NOT `[]` — and `routes/packs.ts` says `children: undefined`
  // for this same case for the same reason. `[]` is TRUTHY, so "no children"
  // would then be riding one detail of a third-party component: SkyNavDrawer
  // happens to branch on `item.children && item.children.length > 0`. Drop the
  // `.length` half of that check upstream — a plausible refactor, and one we do
  // not control — and the Settings row silently becomes an empty category that
  // cannot be clicked, which is exactly the dead door this transform removes.
  // An absent property cannot be misread by any of those variants.
  const children = showSetupDoor ? [...CRM_SETTINGS_CHILDREN] : undefined;
  return {
    ...section,
    items: section.items.map((item) =>
      item.id === NAV_IDS.SETTINGS ? { ...item, children } : item,
    ),
  };
}

/**
 * The CRM-mode sidebar: the operator pack's manifest sections, then the two
 * always-on shared sections (Settings + Support), with Settings de-duplicated
 * against the Setup hub. Extracted from the component so the composition is
 * testable against the REAL builders — the component calls exactly this.
 */
export function composeCrmModeSections(
  packSections: NavSection[],
  baseSections: NavSection[],
  opts: CrmSettingsOptions = {},
): NavSection[] {
  return [
    ...packSections,
    ...baseSections
      .filter((s) => s.header === "Settings" || s.header === "Support")
      .map((s) => (s.header === "Settings" ? withCrmSettingsChildren(s, opts) : s)),
  ];
}

/** Shared with the header toggle, which references it via `aria-controls`. */
export const MOBILE_DRAWER_ID = "sky-mobile-nav-drawer";

const SidebarSky = ({
  isMobile = false,
  isOpen,
  onOpenChange,
  onCollapseChange,
}: SidebarSkyProps) => {
  const authState = useSelector((state: IRootReducerState) => state.authReducer);
  const { isPlatformAdmin, isTenantAdmin, isInstanceAdmin } = authState.permissions;
  const isFullAccess = authState.isFullAccess;
  const accessToken = authState.accessToken;
  const { getQueryString, getInstanceId } = useMsalAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get current instance ID for permission resolution
  const instanceId = getInstanceId();

  // Fetch RBAC permissions for current user (with instance-specific config)
  // Skip until auth token is available to avoid a non-retryable 401
  const { data: rbacPermissions } = useGetMyPermissionsQuery(
    instanceId ? { instanceId } : undefined,
    { skip: !accessToken },
  );

  // Fetch sidebar config from platform_config
  const { data: sidebarConfig, isLoading: sidebarConfigLoading } = useGetConfigSchemaQuery(
    { category: "sidebar", data: "tabs", instanceId: instanceId || "" },
    { skip: !instanceId || !accessToken },
  );

  // Platform `manageSettings` permission, admin-bypassed. Feeds
  // PROTECTED_NAV_GATES inside buildNavSectionsFromConfig so the config-driven
  // sidebar hides Settings sub-tabs a non-admin cannot manage (which 403/redirect
  // on click). Read off the SAME `useGetMyPermissionsQuery` response that supplies
  // rbacNavigation below (rather than a second usePermissionState query keyed to a
  // possibly-different instanceId), so canManageSettings and rbacNavigation stay
  // consistent. Undefined while the query loads → fail-closed (the tab appears a
  // beat late, never a dead door) — the same tradeoff the Setup hub already makes.
  const isAdmin = isTenantAdmin || isPlatformAdmin || isInstanceAdmin;

  // Pack-aware view mode (Day 3 PM). The DEFAULT path is the existing
  // sidebar, untouched. Critical invariant: the 5 provisioned Sky
  // Advisor tenants have ZERO installed packs → currentMode resolves to
  // 'lead_gen_only' and hasOperatorPack is false → the else branch
  // returns baseSections verbatim. The CRM view engages ONLY for a
  // tenant that actually has an operator_crm pack installed AND is
  // currently viewing CRM mode; it shows that pack's manifest-driven
  // section(s) plus the always-on Settings / Help+Support sections,
  // hiding the Advisor marketing surfaces. (Toggling back to Advisor →
  // else branch → full Advisor sidebar; the toggle lives in the header.)
  //
  // ⚠️ Read BEFORE permissionFlags (H3 Inc-2): `hasFeature` feeds
  // PROTECTED_NAV_GATES, so it has to be resolved before the flags object is
  // built. Hook ORDER is stable across renders, which is all React requires.
  const { hasOperatorPack, currentMode, packs, terms, hasFeature, isResolvingMode } = usePacks();

  // Build nav sections — use platform config if available, fall back to hardcoded.
  // IMPORTANT: stick with hardcoded while config is loading to avoid
  // SkyNavDrawer receiving different section structures mid-render
  // (causes controlled/uncontrolled flip in Fluent UI).
  const permissionFlags = {
    isFullAccess,
    isTenantAdmin,
    isPlatformAdmin,
    isInstanceAdmin,
    rbacNavigation: rbacPermissions?.navigation,
    canManageSettings: rbacPermissions?.permissions?.["manageSettings"] === true || isAdmin,
    // H3 Inc-2. The literal is typed via CLOSE_REASONS_FEATURE above rather than
    // inlined: `hasFeature` takes a bare `string`, so a typo here would compile
    // and silently return false forever.
    hasCloseReasonsFeature: hasFeature(CLOSE_REASONS_FEATURE),
    hasPipelineFunnelFeature: hasFeature(PIPELINE_FUNNEL_FEATURE),
  };

  const useConfig =
    !sidebarConfigLoading && sidebarConfig?.schema && sidebarConfig.schema.length > 0;
  const baseSections = useConfig
    ? buildNavSectionsFromConfig(sidebarConfig!.schema as SidebarConfigItem[], permissionFlags)
    : buildNavSections(permissionFlags);

  const crmActive = currentMode === "operator_crm" && hasOperatorPack && !!instanceId;

  // The CRM ROLE, for the Setup door (D2). Deliberately the SAME hook every CRM
  // admin surface asks, so there is one implementation of this question.
  //
  // ⚠️ The empty-string argument is what SKIPS the request, and it is not
  // cosmetic: `useCrmRoleState(undefined)` falls back to `getInstanceIdFromUrl()`
  // and would fetch `/crm/me` on EVERY page load for the five provisioned
  // Advisor-only tenants, which have no CRM identity to resolve and whose
  // sidebar this branch never reaches. `""` short-circuits the query and
  // resolves to `crmRole = null`, which keeps the door — the Advisor path stays
  // byte-identical, which is this file's standing invariant.
  const { crmRole, crmRoleStatus } = useCrmRoleState(crmActive ? (instanceId ?? "") : "");

  // Pack-manifest nav (Phase-0 pack spine): the CRM section(s) come off
  // the wire (packsApi sidebarSections — the manifest is the single
  // source of truth; the deleted seniorLivingNav.ts mirror used to
  // hardcode this). Only operator_crm packs contribute sections here;
  // Advisor-mode nav stays on the RBAC-gated sidebarConfig path.
  const operatorPackSections = packs
    .filter((p) => p.mode === "operator_crm")
    // `?? []` — defensive against test mocks / cached responses predating
    // the sidebarSections field.
    .flatMap((p) => p.sidebarSections ?? []);

  // Nav-tab unread badge (D6): the header icon is gone; the count rides the Messenger
  // nav item. The sidebar renders OUTSIDE NotificationEventProvider, so this polls (30s)
  // and rides the CrmUnreadCount tag (mark-read / reply / assign invalidate it).
  // Selected community (top-bar) scopes the badge to match the filtered Main list (Option A).
  // Straight slice read (the header already populates it) = no extra request; the count query
  // re-keys by it, so a specific community shows that community's Main-unread, All = global.
  const selectedCommunityId = useSelector((s: RootState) => s.communityReducer.selectedCommunityId);
  const { data: crmUnread } = useGetCrmUnreadCountQuery(
    { instanceId: instanceId || "", communityId: selectedCommunityId ?? undefined },
    { skip: !crmActive, pollingInterval: 30000 },
  );
  const crmUnreadCount = crmUnread?.count ?? 0;

  // Yardi Resolution Center count (WelcomeHome parity: "a red notification icon
  // to the right of the Occupancy and Resolution Center with a number indicating
  // how many residents need to be resolved").
  //
  // Supplied through the manifest's badge_source mechanism rather than as a
  // special case — the comment below records that badge_source REPLACED the old
  // withMessengerBadge special-case, and re-adding one would undo that on
  // purpose. The count is wired here; the badge appears the moment the operator
  // pack's Occupancy item declares `badge_source: "yardi_resolution"`, which is
  // a manifest (data) change, not a code one. Until then this resolves to 0 and
  // renders nothing.
  //
  // Gated on manageSettings because the resolution-queue endpoint is, so an
  // ungated call would 403 on every sidebar render for counselors. Reuses the
  // flag this component ALREADY computes — reaching for useHasPermission here
  // pulled in useAuth's `capabilities`, which the sidebar's own tests do not
  // populate, and broke seven of them for a fact already in scope.
  const canSeeYardiQueue = permissionFlags.canManageSettings;

  // Is Yardi set up for this tenant at all? The pack manifest is static per
  // vertical, so it offers "Yardi Resolution" to every senior-living tenant —
  // including the ones that have never configured Yardi, for whom the page can
  // only say so. The manifest cannot know; this can.
  //
  // Returns { integration: null } when none exists, so absence is a value
  // rather than an error. Skipped for operators without manageSettings, which
  // costs nothing: every Yardi route is manageSettings-gated, so the item is
  // hidden for them either way, and an ungated call would 403 on every render.
  const { data: yardiIntegration } = useGetYardiIntegrationQuery(
    { instanceId: instanceId || "" },
    { skip: !crmActive || !instanceId || !canSeeYardiQueue },
  );
  const hasYardiIntegration = Boolean(yardiIntegration?.integration);

  const { data: yardiResolution } = useGetYardiResolutionQueueQuery(
    // limit: 1 — the sidebar renders a NUMBER. Unbounded, this pulled up to 200
    // complete Yardi payloads (date of birth, gender, marital status, address)
    // across the wire every 60 seconds, for every signed-in operator, purely to
    // read `count` off the response. The count is a window total and does not
    // depend on how many rows come back with it.
    { instanceId: instanceId || "", limit: 1 },
    {
      // No integration means no queue to poll — this ran every 60s against a
      // tenant that could never have a row in it.
      skip: !crmActive || !instanceId || !canSeeYardiQueue || !hasYardiIntegration,
      pollingInterval: 60000,
    },
  );
  const yardiResolutionCount = yardiResolution?.count ?? 0;

  // Undefined (not an empty set) when nothing is hidden, so the common path
  // allocates nothing and buildPackNavSections skips the lookup entirely.
  const hiddenPackRoutes = useMemo(
    () => (hasYardiIntegration ? undefined : new Set([ROUTES.CRM_YARDI_RESOLUTION])),
    [hasYardiIntegration],
  );

  // While the installed packs have not resolved, the mode is unknown and
  // `currentMode` is merely the Advisor default — rendering `baseSections` then
  // flashes the Advisor menu on a CRM tenant before the CRM menu swaps in.
  // Render an EMPTY (neutral) nav until the mode is known. Once resolved, both
  // paths below are unchanged (an Advisor-only tenant gets `baseSections`).
  const sections = isResolvingMode
    ? []
    : currentMode === "operator_crm" && hasOperatorPack
      ? composeCrmModeSections(
          // Badge counts keyed by the manifest's badge_source ids —
          // replaces the old withMessengerBadge special-case.
          buildPackNavSections(
            operatorPackSections,
            { inbox_unread: crmUnreadCount, yardi_resolution: yardiResolutionCount },
            terms,
            hiddenPackRoutes,
          ),
          baseSections,
          { showSetupDoor: showsCrmSetupDoor(crmRole, crmRoleStatus) },
        )
      : baseSections;

  // Get current selection based on route. /crm/* now resolves through
  // routeMapping (NAV_IDS.CRM_* + ROUTE_PATTERNS) like every other route
  // — no path-as-id special-case.
  const queryString = getQueryString();
  const { selectedId, selectedCategoryId } = getSelectedNavItem(location.pathname, queryString);

  // Handle nav item selection
  const handleNavSelect = (itemId: string) => {
    const route = getNavRoute(itemId as NavId);
    if (route) {
      navigate(route + queryString);
      // Close mobile sidebar after navigation
      if (isMobile && onOpenChange) {
        onOpenChange(false);
      }
    }
  };

  const sidebarFooter = (
    <SkyCap2
      block
      align="center"
    >
      Copyright {new Date().getFullYear()}{" "}
      <SkyLink
        href="https://skypoint.ai/"
        target="_blank"
      >
        Skypoint.
      </SkyLink>{" "}
      All Rights Reserved.
    </SkyCap2>
  );

  // Mobile sidebar with backdrop overlay
  if (isMobile) {
    if (!isOpen) return null;

    return (
      <>
        {/* Backdrop */}
        <div
          data-testid="mobile-backdrop"
          className="fixed inset-0 bg-black/50 z-999"
          onClick={() => onOpenChange?.(false)}
        />
        {/* Drawer */}
        <div
          // The id the header's toggle points `aria-controls` at, so the button
          // names what it expands rather than just claiming a state.
          id={MOBILE_DRAWER_ID}
          // `flex flex-col min-h-0` makes this fixed wrapper a bounded flex
          // column so the drawer's internal `flex-1 overflow-y-auto` scroll
          // region gets a definite height and scrolls (min-h-0 breaks the
          // default min-height:auto that would otherwise let it overflow).
          className="fixed left-0 z-1000 flex flex-col min-h-0 animate-in slide-in-from-left duration-200"
          // Start the mobile drawer below the app-top banner strip (inline
          // style avoids Tailwind mangling the var() dashes in an arbitrary
          // calc; these classes carry no !important so inline wins cleanly).
          style={{
            top: "var(--app-banner-h, 0px)",
            height: "calc(100vh - var(--app-banner-h, 0px))",
          }}
        >
          <SkyNavDrawer
            sections={sections}
            selectedId={selectedId}
            selectedCategoryId={selectedCategoryId}
            onSelect={handleNavSelect}
            size="medium"
            footer={sidebarFooter}
            className="flex! flex-col! h-full! min-h-0!"
          />
        </div>
      </>
    );
  }

  // Desktop sidebar - visible on md+ screens.
  //
  // `#sky-sidebar` stays IN-FLOW and full-width (its origin/qa box), because 8
  // legacy consumers read `#sky-sidebar.offsetWidth` (CommonBreadcrumb,
  // getTopBarWidth, useWindowDimensions, Wizard, createTenant, Tenants, …) and
  // expect the viewport-width value. The fixed positioning + banner offset live
  // on the INNER wrapper below, so the drawer still slides beneath the app-top
  // banner without changing `#sky-sidebar`'s own geometry.
  return (
    <div
      id="sky-sidebar"
      className="hidden! md:block!"
    >
      {/* Inner wrapper carries the fixed positioning and the app-top banner
          offset. The inline style reads --app-banner-h with a 0px fallback, so
          when no banner shows top stays 50px and height stays calc(100vh-50px)
          — byte-identical to pre-fix. Inline wins cleanly because no
          `!important` utility competes on top/height, and it avoids Tailwind
          mangling the var() dashes inside an arbitrary calc.

          Restores the scroll-chain the deleted
          `#sky-sidebar > * { display:flex; flex-direction:column }` rule used to
          provide. The wrapper is a bounded flex column (definite height →
          `flex flex-col min-h-0`); the SkyNavDrawer root is a `flex-1 min-h-0`
          flex child so it gets a definite height. CRITICAL: the drawer root
          `<nav>` computes `display:block` in this app (its library `flex` class
          is out-ranked once the old `#sky-sidebar > *` rule is gone), so we ALSO
          force `flex! flex-col!` on it — otherwise its inner
          `flex-1 overflow-y-auto` region is not a flex item, never shrinks below
          content, overflows, and is clipped by the nav's `overflow-hidden`,
          leaving the bottom items (Settings→Session, Integrations) unreachable.
          Verified live: forcing the nav to flex-column makes the region shrink
          and scroll. */}
      <div
        className="fixed! left-0! z-810! flex! flex-col! min-h-0!"
        style={{
          top: "calc(50px + var(--app-banner-h, 0px))",
          height: "calc(100vh - 50px - var(--app-banner-h, 0px))",
        }}
      >
        <SkyNavDrawer
          sections={sections}
          selectedId={selectedId || ""}
          selectedCategoryId={selectedCategoryId || ""}
          onSelect={handleNavSelect}
          size="medium"
          collapsible
          onCollapseChange={onCollapseChange}
          footer={sidebarFooter}
          className="flex! flex-col! flex-1! min-h-0!"
        />
      </div>
    </div>
  );
};

export default SidebarSky;
